import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import TelegramIcon from "@mui/icons-material/Telegram"
import {
    AlertTitle,
    Box,
    Collapse,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    useTheme,
} from "@mui/material"
import { Dispatch, ReactElement, SetStateAction } from "react"
import { useForm } from "react-hook-form"
import { KeyedMutator } from "swr"

import GenAndDemandConditions from "./genAndDemandConditions"
import GenOrDemandOnlyConditions from "./genOrDemandOnlyConditions"
import {
    ApplicationClasses,
    DnoResponses,
    LimitReasons,
} from "../../../constants/enums"
import useDnoResponse from "../../../services/middleware/hooks/dnoInterface/protected/useDnoResponse"
import { TGetSingleApplication } from "../../../services/middleware/hooks/dnoInterface/protected/useDnoSingleApplication"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppButton from "../../buttons/appButton"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import AppDialog from "../../dialog/appDialog"
import ValidatedRadioGroup from "../../inputs/validated/validatedRadioGroup"
import ValidatedTextField from "../../inputs/validated/validatedTextField"
import { AppCardHeader } from "../../surfaces/appCard"
import DisplayIf from "../../wrappers/displayIf"

export const MAX_COMMENT_LENGTH = 1000

export const DNO_RESPONSES = {
    Approve: DnoResponses.Approve,
    "Approve Conditionally": DnoResponses.ConditionalApprove,
    "Further Information Required": DnoResponses.furtherInformation,
}

export const LIMIT_REASONS = {
    [LimitReasons.cableReplacementRequired]:
        LimitReasons.cableReplacementRequired,
    [LimitReasons.cutOutReplacementRequired]:
        LimitReasons.cutOutReplacementRequired,
    [LimitReasons.fuseReplacementRequired]:
        LimitReasons.fuseReplacementRequired,
    [LimitReasons.unLoopingRequired]: LimitReasons.unLoopingRequired,
    [LimitReasons.furtherInformation]: LimitReasons.furtherInformation,
    [LimitReasons.voltageStatutoryLimits]: LimitReasons.voltageStatutoryLimits,
}

// Register names
export const RESPONSE_REG = "response"
export const GEN_LIMIT_REG = "genLimit"
export const DEMAND_LIMIT_REG = "demandLimit"
export const GEN_LIMIT_REASON_REG = "genLimitReasons"
export const DEMAND_LIMIT_REASON_REG = "demandLimitReasons"
export const COMMENT_REG = "comment"

interface IDnoResponseForm {
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    applicationId: string
    applicationDate: string
    applicationAddress: string
    mutate: KeyedMutator<TGetSingleApplication>
    applicationClass: ApplicationClasses
}

/**
 * Dialog where DNO users can submit responses to escalated applications.
 * This is an uncontrolled form, registered with react-hook-form.
 * If a component is mounted, it is automatically registered with react-hook-form.
 */
const DnoResponseForm = ({
    isOpen,
    setIsOpen,
    applicationId,
    applicationClass,
    applicationDate,
    applicationAddress,
    mutate,
}: IDnoResponseForm): ReactElement => {
    const theme = useTheme()
    const { isSending, error, postResponse } = useDnoResponse()
    const { control, handleSubmit, watch } = useForm({
        shouldUnregister: true,
    })

    const onSuccessFullSubmit = (): void => {
        mutate(undefined)
        setIsOpen(false)
    }
    const submitResponse = (): void => {
        postResponse(
            applicationId,
            watch(RESPONSE_REG),
            watch(GEN_LIMIT_REG),
            watch(DEMAND_LIMIT_REG),
            watch(GEN_LIMIT_REASON_REG),
            watch(DEMAND_LIMIT_REASON_REG),
            watch(COMMENT_REG),
            onSuccessFullSubmit
        )
    }
    const isApproveWithCondition =
        watch(RESPONSE_REG) === DnoResponses.ConditionalApprove

    return (
        <AppDialog isOpen={isOpen}>
            <DialogTitle>
                <AppCardHeader
                    sx={{ p: 0 }}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    title={
                        <>
                            Make Decision for Application at{" "}
                            <Box
                                color={theme.palette.secondary.main}
                                display="inline"
                            >
                                {applicationAddress}
                            </Box>{" "}
                        </>
                    }
                    subheader={applicationId}
                />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={GRID_SPACING}>
                    <DialogContentText>
                        This application was escalated to you on{" "}
                        {applicationDate}. Use this form to issue your final
                        decision. On submission, an email will be sent to the
                        installer notifying them of your decision. No
                        communications will be sent to the customer.
                    </DialogContentText>
                    <Collapse in={!!error} mountOnEnter>
                        <AppAlert severity={error?.level}>
                            <AlertTitle>{error?.message}</AlertTitle>
                            {error?.displayDetail}
                        </AppAlert>
                    </Collapse>
                    <ValidatedRadioGroup
                        registerName={RESPONSE_REG}
                        label="Choose your outcome"
                        control={control}
                        radioOptions={DNO_RESPONSES}
                        required
                    />
                    <DisplayIf
                        condition={
                            applicationClass === ApplicationClasses.genGeneral
                        }
                    >
                        <GenOrDemandOnlyConditions
                            display={isApproveWithCondition}
                            control={control}
                            isGenMode
                        />
                    </DisplayIf>
                    <DisplayIf
                        condition={
                            applicationClass ===
                            ApplicationClasses.demandGeneral
                        }
                    >
                        <GenOrDemandOnlyConditions
                            display={isApproveWithCondition}
                            control={control}
                            isGenMode={false}
                        />
                    </DisplayIf>
                    <DisplayIf
                        condition={
                            applicationClass ===
                            ApplicationClasses.genDemandGeneral
                        }
                    >
                        <GenAndDemandConditions
                            display={isApproveWithCondition}
                            control={control}
                        />
                    </DisplayIf>
                    <ValidatedTextField
                        control={control}
                        required={false}
                        registerName={COMMENT_REG}
                        label={`Comments (${MAX_COMMENT_LENGTH} characters max)`}
                        placeholder="Enter any optional comments..."
                        tooltip="These comments will be included on the automated email sent to both the installer and the customer (if your DNO has opted in to sending emails from Connect Direct). Speak to your company admin if you are unsure"
                        multiline
                        customRules={{
                            maxLength: {
                                value: MAX_COMMENT_LENGTH,
                                message: `Max permitted characters: ${MAX_COMMENT_LENGTH}`,
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AppButton
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    disabled={isSending}
                    onClick={() => setIsOpen(false)}
                >
                    Back
                </AppButton>
                <AppLoadingButton
                    color="success"
                    variant="contained"
                    endIcon={<TelegramIcon />}
                    loading={isSending}
                    onClick={handleSubmit(submitResponse)}
                >
                    Submit
                </AppLoadingButton>
            </DialogActions>
        </AppDialog>
    )
}

export default DnoResponseForm
